import { RecurringTransferEndType } from '@fruitful/types';
import { addDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { formatUTCDateToISODateString } from './dateUtils';

export const getLastFourAccountNumber = (accountNumber?: string): string => {
    const len = accountNumber?.length ?? 0;
    if (len < 4 || accountNumber == null) {
        return '';
    }
    return accountNumber.slice(len - 4);
};

export const getRecurringTransferEndText = (
    endType?: RecurringTransferEndType,
    formattedEndDate?: string | null,
    endCount?: number | null,
): string => {
    if (endType === RecurringTransferEndType.NEVER) {
        return 'Never';
    }

    if (formattedEndDate != null) {
        return formattedEndDate;
    }

    if (endCount != null) {
        return `After ${endCount} transfers `;
    }

    return '';
};

// From IANA database. For use with date-fns-tz package when a fixed time zone is required
// As an example, to pull the current interest rate we need to make dates (YYYY-MM-DD) to the
// current time. Using this time zone offset helps to change the bank date at a fixed point in time globally
export const BANK_TIME_ZONE = 'America/New_York';
export const BANK_EOD_HOUR = 17; // 5pm ET marks the next "day" starting

// Returns a Date object with time set to midnight that matches how posted_date is set for new transactions.
// Transactions created after 5pm ET have a posted date of the next day irrespective of weekends/holidays.
export const currentPostedDate = () => {
    const currentDateStringInBankTimeZone = formatInTimeZone(
        new Date(),
        BANK_TIME_ZONE as string,
        // 'T' (ISO date format) is required for older Safari versions. Date-fns-tz docs do not use 'T' in examples and instead uses a space. Newer versions of Safari, Google, Chrome are all OK with a space instead of 'T'
        "yyyy-MM-dd'T'HH:mm:ss", // excludes time zone component.
    );
    const nowFromBankTimeZone = new Date(currentDateStringInBankTimeZone); // parse date in local computer time zone

    const isBankTimeAfter5pmET =
        nowFromBankTimeZone.getHours() >= BANK_EOD_HOUR; /* 17 = 5pm */

    if (isBankTimeAfter5pmET) {
        return new Date(addDays(nowFromBankTimeZone, 1).toDateString());
    } else {
        return new Date(nowFromBankTimeZone.toDateString());
    }
};

let TESTING_POSTED_DATE_ISO_DATE_STRING_OVERRIDE: undefined | string;
export const setCurrentPostedDateIsoDateStringOverrideorTesting = (
    isoDateString: string,
): void => {
    TESTING_POSTED_DATE_ISO_DATE_STRING_OVERRIDE = isoDateString;
};

export const currentPostedDateAsIsoDateString = (): string => {
    if (TESTING_POSTED_DATE_ISO_DATE_STRING_OVERRIDE !== undefined) {
        return TESTING_POSTED_DATE_ISO_DATE_STRING_OVERRIDE;
    }

    const currentPostedDateObject = currentPostedDate();
    return formatUTCDateToISODateString(currentPostedDateObject);
};

interface SingleInterestRate {
    startDateInclusive: string;
    endDateExclusive: string | undefined;
    formattedApy: string;
    interestRate: number;
}

// This needs to be kept in sync with Synctera.
const interestRatesAndApyReference: SingleInterestRate[] = [
    {
        startDateInclusive: '2025-11-01',
        endDateExclusive: undefined,
        formattedApy: '4.00%',
        interestRate: 392,
    },
    {
        startDateInclusive: '2023-01-01',
        endDateExclusive: '2025-11-01',
        formattedApy: '5.00%',
        interestRate: 488,
    },
];

export const currentApyAndInterestRate = (): {
    formattedApy: string;
    interestRate: number;
} => {
    const currentPostedDate = currentPostedDateAsIsoDateString();
    const relevantRate = interestRatesAndApyReference.find(
        (r) =>
            r.startDateInclusive <= currentPostedDateAsIsoDateString() &&
            (r.endDateExclusive === undefined ||
                r.endDateExclusive > currentPostedDate),
    );

    if (relevantRate === undefined) {
        throw new Error(
            `Element '${currentPostedDateAsIsoDateString()}' not found in the interestRatesAndApyReference.`,
        );
    }
    const { formattedApy, interestRate } = relevantRate;

    return {
        formattedApy,
        interestRate,
    };
};

export const currentApy = (): string => {
    return currentApyAndInterestRate().formattedApy;
};
